'use client';

import { Button } from '@/components/ui/buttons/Button';
import { useTranslation } from '@/features/i18n';
import { Link } from '@/ui/Link';
import { Input } from '@/ui/form/Input';
import { Label } from '@/ui/form/Label';
import { ApplicationLogo } from '@/ui/images/ApplicationLogo';
import { logger } from '@/utils/loggerUtils';
import {
  appendReturnUrl,
  getReturnUrlFromParams,
  storeRedirectUrl,
} from '@/utils/redirectUtils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuthentication } from '@vimond/features/authentication';
import { getEnvVar } from '@vimond/utils/env';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { VippsSignInButton } from '../buttons/VippsSignInButton';
import { SetNewPasswordForm } from './SetNewPasswordForm';
import { SignInConfirmForm } from './SignInConfirmForm';

const CHANGE_PASSWORD = 'CHANGE_PASSWORD';
const VERIFY_TOTP = 'VERIFY_TOTP';

type NextStep = 'CHANGE_PASSWORD' | 'VERIFY_TOTP';

export const SignInForm = () => {
  const { signIn } = useAuthentication();
  const searchParams = useSearchParams();
  const returnUrl = getReturnUrlFromParams(searchParams?.toString());

  // Store returnUrl in sessionStorage so it persists through the auth flow
  useEffect(() => {
    if (returnUrl) {
      storeRedirectUrl(returnUrl);
    }
  }, [returnUrl]);

  const { t } = useTranslation();

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [nextStep, setNextStep] = useState<NextStep>();
  // time-based one time password / code required? (MFA - multi-factor authentication)
  const [totpQRCode, setTotpQRCode] = useState<string>('');

  const FormSchema = z.object({
    email: z.string().email(t('auth.validate.invalid-email-address')),
    password: z
      .string()
      .min(8, t('auth.validate.password-must-be-at-least-8-charaters-long')),
  });

  type FormSchemaType = z.infer<typeof FormSchema>;
  const {
    formState: { errors, isDirty, isLoading, isSubmitting },
    handleSubmit,
    register,
  } = useForm<FormSchemaType>({
    resolver: zodResolver(FormSchema),
    values: {
      email: '',
      password: '',
    },
  });

  const onSubmit: SubmitHandler<FormSchemaType> = async (data) => {
    const { email, password } = data;

    // Clear any previous error messages
    setErrorMessage('');

    try {
      const res = await signIn({ email, password });

      if (res.isSignedIn) {
        // Successful login - redirect to profiles page, preserving returnUrl
        const profilesUrl = returnUrl
          ? appendReturnUrl('/profiles/whoiswatching', returnUrl)
          : '/profiles/whoiswatching';
        window.location.href = profilesUrl;
      } else if (
        res.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP'
      ) {
        const totpSetupDetails = res.nextStep.totpSetupDetails;
        // get title from the html title
        const title = document.title;
        const uri = totpSetupDetails.getSetupUri(title, email);
        setTotpQRCode(uri.href);
        setNextStep(VERIFY_TOTP);
      } else if (res.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setNextStep(VERIFY_TOTP);
      } else if (res.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        window.location.href = '/auth/signup/confirm';
      } else if (
        res.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      ) {
        setNextStep(CHANGE_PASSWORD);
      } else {
        logger.warn('Unhandled auth flow step:', res);
      }
    } catch (err) {
      logger.error('Login error:', err);
      setErrorMessage(t('auth.login-failed'));
    }
  };

  return (
    <section>
      <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto lg:h-screen lg:py-0">
        <ApplicationLogo />
        <div className="w-full border rounded-lg shadow border-background-border bg-background-secondary sm:max-w-md md:mt-0 xl:p-0">
          {nextStep === VERIFY_TOTP && (
            <SignInConfirmForm
              totpQRCode={totpQRCode}
              onNotAuthorizedException={() => setTotpQRCode('')}
            />
          )}
          {nextStep === CHANGE_PASSWORD && <SetNewPasswordForm />}
          {!nextStep && (
            <div className="p-6 space-y-4 sm:p-8 md:space-y-6">
              <h1 className="text-xl font-bold leading-tight tracking-tight text-center md:text-2xl">
                {t('auth.sign-in-to-your-account')}
              </h1>

              {getEnvVar('NEXT_PUBLIC_VIPPS_LOGIN_ENABLED') === 'true' && (
                <VippsSignInButton onSignInError={setErrorMessage} />
              )}

              <form
                className="space-y-4 md:space-y-6"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div>
                  <Label htmlFor="email">{t('auth.email-address')}</Label>
                  <div className="mt-2">
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      {...register('email')}
                      placeholder={t('auth.email-placeholder')}
                      aria-describedby="email-error"
                    />
                  </div>
                  <p
                    id="email-error"
                    className="text-red-500"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    {errors.email?.message}
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">{t('auth.password')}</Label>
                    <div className="text-sm">
                      <Link
                        href="/auth/password"
                        className="font-semibold text-text-highlight"
                      >
                        {t('auth.forgot-password')}
                      </Link>
                    </div>
                  </div>
                  <div className="mt-2">
                    <Input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      {...register('password')}
                      placeholder="********"
                      aria-describedby="password-error"
                    />
                  </div>
                  <p
                    id="password-error"
                    className="text-red-500"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    {errors.password?.message}
                  </p>
                </div>

                <Button
                  variant="primary"
                  size="sm"
                  disabled={isLoading || isSubmitting || !isDirty}
                  type="submit"
                  className="w-full"
                >
                  {t('sign-in')}
                </Button>
                {errorMessage && <p className="text-red-500">{errorMessage}</p>}
              </form>

              <p className="mt-10 text-sm text-center text-text-secondary">
                {t('auth.dont-have-an-account')}{' '}
                <Link
                  href="/auth/signup"
                  className="font-semibold leading-6 text-text-highlight"
                >
                  {t('auth.sign-up-now')}
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
